import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore';
import { getDownloadURL, ref } from 'firebase/storage';
import { auth, db, storage } from './firebase';
import { firestoreSnapshotListenError } from './firestoreListenDebug';
import type {
  ContractorAgreement,
  ContractorAgreementFields,
  ContractorApplicant,
  ContractorApplicantFile,
  ContractorApplicantStatus,
} from '../types/contractorOnboard';
import type { JobOfferSection, JobOfferSignature } from '../types/hiring';
import { assembleContractorAgreementSections } from './contractorAgreement';
import { dayRateFromAgreementFields } from './contractorAgreementDayRate';
import { crmAuthedFetch } from './crmAuthedFetch';
import {
  createContractorOnboardSlug,
  mergeApplicantFromCommit,
  normalizeContractorEmail,
  uniqueIds,
} from './contractorOnboard';

export const CONTRACTOR_APPLICANTS = 'contractorApplicants';
export const CONTRACTOR_AGREEMENTS = 'contractorAgreements';

function parseDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    return (value as { toDate: () => Date }).toDate();
  }
  const d = new Date(value as string);
  return Number.isNaN(d.getTime()) ? null : d;
}

function parseFile(raw: unknown): ContractorApplicantFile | null {
  if (!raw || typeof raw !== 'object') return null;
  const f = raw as Record<string, unknown>;
  if (typeof f.storagePath !== 'string' || !f.storagePath) return null;
  return {
    fileName: String(f.fileName || 'file'),
    storagePath: f.storagePath,
    contentType: String(f.contentType || 'application/octet-stream'),
    sizeBytes: typeof f.sizeBytes === 'number' ? f.sizeBytes : Number(f.sizeBytes) || 0,
    uploadedAt: parseDate(f.uploadedAt) || new Date(),
  };
}

function parseSignature(raw: unknown): JobOfferSignature | null {
  if (!raw || typeof raw !== 'object') return null;
  const s = raw as Record<string, unknown>;
  const signedBy = String(s.signedBy || '');
  const dataUrl = typeof s.dataUrl === 'string' ? s.dataUrl : '';
  if (!signedBy && !dataUrl) return null;
  return {
    dataUrl,
    signedBy,
    signedAt: parseDate(s.signedAt) || new Date(),
    title: typeof s.title === 'string' ? s.title : undefined,
    ipAddress: typeof s.ipAddress === 'string' && s.ipAddress.trim() ? s.ipAddress.trim() : undefined,
    userAgent: typeof s.userAgent === 'string' && s.userAgent.trim() ? s.userAgent.trim() : undefined,
    source: typeof s.source === 'string' && s.source.trim() ? s.source.trim() : undefined,
    grandfathered: s.grandfathered === true,
  };
}

export function parseContractorApplicant(id: string, raw: Record<string, unknown>): ContractorApplicant {
  return {
    id,
    firstName: String(raw.firstName || ''),
    lastName: String(raw.lastName || ''),
    email: String(raw.email || ''),
    emailNormalized: normalizeContractorEmail(String(raw.emailNormalized || raw.email || '')),
    phone: typeof raw.phone === 'string' ? raw.phone : undefined,
    status: (raw.status as ContractorApplicantStatus) || 'submitted',
    sourceCommitIds: Array.isArray(raw.sourceCommitIds) ? raw.sourceCommitIds.map(String) : [],
    jobIds: Array.isArray(raw.jobIds) ? raw.jobIds.map(String) : [],
    lastJobId: typeof raw.lastJobId === 'string' ? raw.lastJobId : null,
    lastJobTitle: typeof raw.lastJobTitle === 'string' ? raw.lastJobTitle : null,
    publicSlug: String(raw.publicSlug || ''),
    w9: parseFile(raw.w9),
    photoId: parseFile(raw.photoId),
    w9ApprovedAt: parseDate(raw.w9ApprovedAt),
    w9ApprovedBy: typeof raw.w9ApprovedBy === 'string' ? raw.w9ApprovedBy : null,
    photoIdApprovedAt: parseDate(raw.photoIdApprovedAt),
    photoIdApprovedBy: typeof raw.photoIdApprovedBy === 'string' ? raw.photoIdApprovedBy : null,
    formsRequestedAt: parseDate(raw.formsRequestedAt),
    formsApprovedAt: parseDate(raw.formsApprovedAt),
    agreementId: typeof raw.agreementId === 'string' ? raw.agreementId : null,
    contractorUserId: typeof raw.contractorUserId === 'string' ? raw.contractorUserId : null,
    createdAt: parseDate(raw.createdAt) || new Date(),
    updatedAt: parseDate(raw.updatedAt) || new Date(),
  };
}

function parseFields(raw: unknown): ContractorAgreementFields {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  return {
    contractorFirstName: String(r.contractorFirstName || ''),
    contractorLastName: String(r.contractorLastName || ''),
    contractorEmail: String(r.contractorEmail || ''),
    jobTitle: String(r.jobTitle || ''),
    scope: String(r.scope || ''),
    payAmount: typeof r.payAmount === 'number' ? r.payAmount : Number(r.payAmount) || 0,
    payUnit: r.payUnit === 'day' ? 'day' : 'hour',
    location: String(r.location || ''),
    ceoName: String(r.ceoName || ''),
    ceoTitle: String(r.ceoTitle || ''),
  };
}

export function parseContractorAgreement(id: string, raw: Record<string, unknown>): ContractorAgreement {
  return {
    id,
    applicantId: String(raw.applicantId || ''),
    slug: String(raw.slug || ''),
    status: (raw.status as ContractorAgreement['status']) || 'draft',
    fields: parseFields(raw.fields),
    sections: Array.isArray(raw.sections) ? raw.sections as JobOfferSection[] : [],
    ceoSignature: parseSignature(raw.ceoSignature),
    contractorSignature: parseSignature(raw.contractorSignature),
    sentAt: parseDate(raw.sentAt),
    signedAt: parseDate(raw.signedAt),
    signedPdfUrl: typeof raw.signedPdfUrl === 'string' ? raw.signedPdfUrl : null,
    signedPdfPath: typeof raw.signedPdfPath === 'string' ? raw.signedPdfPath : null,
    createdBy: String(raw.createdBy || ''),
    createdAt: parseDate(raw.createdAt) || new Date(),
    updatedAt: parseDate(raw.updatedAt) || new Date(),
  };
}

export function subscribeContractorApplicants(
  onData: (rows: ContractorApplicant[]) => void,
): Unsubscribe {
  const q = query(collection(db, CONTRACTOR_APPLICANTS), orderBy('updatedAt', 'desc'));
  return onSnapshot(
    q,
    snap => {
      onData(snap.docs.map(d => parseContractorApplicant(d.id, d.data() as Record<string, unknown>)));
    },
    err => firestoreSnapshotListenError('contractorOnboardFirestore', CONTRACTOR_APPLICANTS, err),
  );
}

export function subscribeLatestContractorAgreement(
  applicantId: string,
  onData: (agreement: ContractorAgreement | null) => void,
): Unsubscribe {
  if (!applicantId) {
    onData(null);
    return () => undefined;
  }
  const q = query(
    collection(db, CONTRACTOR_AGREEMENTS),
    where('applicantId', '==', applicantId),
    orderBy('createdAt', 'desc'),
    limit(8),
  );
  return onSnapshot(
    q,
    snap => {
      const rows = snap.docs
        .map(d => parseContractorAgreement(d.id, d.data() as Record<string, unknown>))
        .filter(a => a.status !== 'voided');
      onData(rows[0] || null);
    },
    err => firestoreSnapshotListenError('contractorOnboardFirestore', CONTRACTOR_AGREEMENTS, err),
  );
}

/** Latest signed crew agreement day rate for a rostered 1099 user, if any. */
export async function getContractorSignedAgreementDayRate(userId: string): Promise<number | null> {
  const uid = String(userId || '').trim();
  if (!uid) return null;

  const appSnap = await getDocs(
    query(collection(db, CONTRACTOR_APPLICANTS), where('contractorUserId', '==', uid), limit(12)),
  );
  const applicants = appSnap.docs
    .map((d) => parseContractorApplicant(d.id, d.data() as Record<string, unknown>))
    .sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());

  for (const applicant of applicants) {
    if (!applicant.agreementId) continue;
    const agreSnap = await getDoc(doc(db, CONTRACTOR_AGREEMENTS, applicant.agreementId));
    if (!agreSnap.exists()) continue;
    const agreement = parseContractorAgreement(agreSnap.id, agreSnap.data() as Record<string, unknown>);
    if (agreement.status !== 'signed') continue;
    const dayRate = dayRateFromAgreementFields(agreement.fields);
    if (dayRate != null) return dayRate;
  }

  return null;
}

export async function findContractorApplicantByEmail(
  email: string,
): Promise<ContractorApplicant | null> {
  const emailNormalized = normalizeContractorEmail(email);
  if (!emailNormalized) return null;
  const snap = await getDocs(
    query(
      collection(db, CONTRACTOR_APPLICANTS),
      where('emailNormalized', '==', emailNormalized),
      limit(1),
    ),
  );
  if (snap.empty) return null;
  return parseContractorApplicant(snap.docs[0].id, snap.docs[0].data() as Record<string, unknown>);
}

export async function ensureContractorApplicantFromCommit(input: {
  commitId: string;
  jobId: string;
  jobTitle?: string | null;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
}): Promise<ContractorApplicant> {
  const existing = await findContractorApplicantByEmail(input.email);
  const merged = mergeApplicantFromCommit({
    existing,
    commitId: input.commitId,
    jobId: input.jobId,
    jobTitle: input.jobTitle,
    firstName: input.firstName,
    lastName: input.lastName,
    email: input.email,
    phone: input.phone,
    publicSlug: existing?.publicSlug || createContractorOnboardSlug(),
  });
  if (existing) {
    await updateDoc(doc(db, CONTRACTOR_APPLICANTS, existing.id), {
      firstName: merged.firstName,
      lastName: merged.lastName,
      phone: merged.phone || '',
      status: merged.status,
      sourceCommitIds: merged.sourceCommitIds,
      jobIds: merged.jobIds,
      lastJobId: merged.lastJobId || null,
      lastJobTitle: merged.lastJobTitle || null,
      updatedAt: serverTimestamp(),
    } as DocumentData);
    return { ...merged, id: existing.id };
  }
  const ref = await addDoc(collection(db, CONTRACTOR_APPLICANTS), {
    firstName: merged.firstName,
    lastName: merged.lastName,
    email: merged.emailNormalized,
    emailNormalized: merged.emailNormalized,
    phone: merged.phone || '',
    status: 'submitted',
    sourceCommitIds: merged.sourceCommitIds,
    jobIds: merged.jobIds,
    lastJobId: merged.lastJobId || null,
    lastJobTitle: merged.lastJobTitle || null,
    publicSlug: merged.publicSlug,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return { ...merged, id: ref.id };
}

export async function requestContractorForms(applicantId: string): Promise<{ emailed: boolean }> {
  const res = await crmAuthedFetch('staffRequestContractorForms', {
    method: 'POST',
    body: JSON.stringify({ applicantId }),
  });
  const json = await res.json().catch(() => ({})) as { success?: boolean; error?: string; emailed?: boolean };
  if (!res.ok || json.success === false) {
    throw new Error(json.error || 'Could not send the W-9 and ID request');
  }
  return { emailed: json.emailed === true };
}

export async function grandfatherContractorIntoPool(input: {
  applicantId: string;
  w9OnFile: boolean;
  photoIdOnFile: boolean;
  agreementSigned: boolean;
  note?: string;
  payAmount?: number;
  payUnit?: 'hour' | 'day';
  jobTitle?: string;
}): Promise<{ contractorUserId: string; agreementId: string }> {
  const res = await crmAuthedFetch('staffGrandfatherContractorIntoPool', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  const json = await res.json().catch(() => ({})) as {
    success?: boolean;
    error?: string;
    contractorUserId?: string;
    agreementId?: string;
  };
  if (!res.ok || json.success === false) {
    throw new Error(json.error || 'Could not grandfather this contractor into the pool');
  }
  return {
    contractorUserId: String(json.contractorUserId || ''),
    agreementId: String(json.agreementId || ''),
  };
}

export async function reviewContractorPacket(
  applicantId: string,
  input: { w9Approved: boolean; photoIdApproved: boolean; reviewerUid: string },
): Promise<void> {
  const payload: Record<string, unknown> = {
    updatedAt: serverTimestamp(),
  };
  if (input.w9Approved) {
    payload.w9ApprovedAt = serverTimestamp();
    payload.w9ApprovedBy = input.reviewerUid;
  }
  if (input.photoIdApproved) {
    payload.photoIdApprovedAt = serverTimestamp();
    payload.photoIdApprovedBy = input.reviewerUid;
  }
  if (input.w9Approved && input.photoIdApproved) {
    payload.status = 'forms_approved';
    payload.formsApprovedAt = serverTimestamp();
  }
  await updateDoc(doc(db, CONTRACTOR_APPLICANTS, applicantId), payload as DocumentData);
}

export async function updateContractorApplicantStatus(
  applicantId: string,
  status: ContractorApplicantStatus,
): Promise<void> {
  await updateDoc(doc(db, CONTRACTOR_APPLICANTS, applicantId), {
    status,
    updatedAt: serverTimestamp(),
  } as DocumentData);
}

export async function deleteContractorApplicant(applicantId: string): Promise<void> {
  await deleteDoc(doc(db, CONTRACTOR_APPLICANTS, applicantId));
}

export async function createContractorAgreementDraft(input: {
  applicantId: string;
  fields: ContractorAgreementFields;
}): Promise<string> {
  const uid = auth.currentUser?.uid || '';
  const ref = await addDoc(collection(db, CONTRACTOR_AGREEMENTS), {
    applicantId: input.applicantId,
    slug: createContractorOnboardSlug(),
    status: 'draft',
    fields: input.fields,
    sections: assembleContractorAgreementSections(input.fields),
    ceoSignature: null,
    contractorSignature: null,
    sentAt: null,
    signedAt: null,
    signedPdfUrl: null,
    createdBy: uid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  await updateDoc(doc(db, CONTRACTOR_APPLICANTS, input.applicantId), {
    agreementId: ref.id,
    updatedAt: serverTimestamp(),
  } as DocumentData);
  return ref.id;
}

export async function saveContractorAgreementDraft(
  id: string,
  input: { fields: ContractorAgreementFields; ceoSignature?: JobOfferSignature | null },
): Promise<void> {
  const payload: Record<string, unknown> = {
    fields: input.fields,
    sections: assembleContractorAgreementSections(input.fields),
    updatedAt: serverTimestamp(),
  };
  if (input.ceoSignature !== undefined) {
    payload.ceoSignature = input.ceoSignature
      ? {
        dataUrl: input.ceoSignature.dataUrl,
        signedBy: input.ceoSignature.signedBy,
        signedAt: input.ceoSignature.signedAt,
        title: input.ceoSignature.title || null,
        ipAddress: input.ceoSignature.ipAddress || null,
        userAgent: input.ceoSignature.userAgent || (typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 240) : null),
        source: input.ceoSignature.source || 'crm',
        grandfathered: input.ceoSignature.grandfathered === true,
      }
      : null;
  }
  await updateDoc(doc(db, CONTRACTOR_AGREEMENTS, id), payload as DocumentData);
}

export async function sendContractorAgreement(id: string, applicantId: string): Promise<void> {
  await updateDoc(doc(db, CONTRACTOR_AGREEMENTS, id), {
    status: 'sent',
    sentAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  } as DocumentData);
  await updateDoc(doc(db, CONTRACTOR_APPLICANTS, applicantId), {
    status: 'agreement_sent',
    agreementId: id,
    updatedAt: serverTimestamp(),
  } as DocumentData);
}

export async function getContractorOnboardFileUrl(storagePath: string): Promise<string> {
  return getDownloadURL(ref(storage, storagePath));
}

export function applicantByEmailMap(applicants: ContractorApplicant[]): Map<string, ContractorApplicant> {
  const map = new Map<string, ContractorApplicant>();
  applicants.forEach(row => {
    map.set(normalizeContractorEmail(row.emailNormalized || row.email), row);
  });
  return map;
}

export function addJobIdToApplicant(applicant: ContractorApplicant, jobId: string): string[] {
  return uniqueIds([...applicant.jobIds, jobId]);
}
